import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";

/**
 * HAFTALIK VARDİYA PROGRAMI — yazdırılabilir A4 DİKEY belge.
 *
 * NEDEN AYRI SAYFA: panonun kendisini yazdırmak işe yaramıyor. Pano bir ekran
 * widget'ı — 158px'lik sütunlar, kaydırma kutusu, 86 kişilik tek hücre. A4 yatayda
 * kullanılabilir genişlik ~277mm; pano 2000px'i aşabildiği için sayfaya sığmıyor,
 * ölçekleyince de okunmaz hâle geliyordu.
 *
 * YERLEŞİM: her VARDİYA dikey bir SÜTUN, aralarında ayraç çizgisi.
 * Gerçek <table> kullanılır — uzun bir liste sayfalar arasında bölündüğünde tarayıcı
 * sütun hizasını ve kenarlıkları korur; flex ya da CSS-columns bunu yapamaz.
 * <thead> her sayfada tekrar eder, ikinci sayfada hangi sütunun hangi vardiya olduğu
 * kaybolmaz. "Sayfaya sığdır" sorunu ölçekleyerek değil DOĞRU YERLEŞİMLE çözülür —
 * metin vektör ve tam boyda kalır.
 *
 * DİKEY (portrait) sayfada kullanılabilir genişlik ~190mm, yatayın üçte ikisi kadar;
 * bu yüzden yazı ölçüleri ve iç boşluklar daraltıldı, uzun adlar kırpılmak yerine
 * `overflow-wrap: anywhere` ile alt satıra sarar.
 *
 * Uygulama kabuğu (sidebar) DIŞINDADIR; yeni sekmede açılır (router.tsx /print/*).
 */
export function ShiftPlanPrintPage() {
  const [sp] = useSearchParams();
  const weekStart = sp.get("weekStart") ?? new Date().toISOString().slice(0, 10);

  /* İçerik seçenekleri — kullanıcı belgede NELERİN yer alacağına karar verir.
     Varsayılanlar "tam belge"; kapatmak isteyen kapatır. URL ile de gelebilir
     (pano JPEG için ?export=jpeg gönderir). */
  const [optCompany, setOptCompany] = useState(true);
  const [optOff, setOptOff] = useState(true);          // İstirahat sütunu
  const [optCounts, setOptCounts] = useState(true);    // kişi sayıları
  const [optNumbers, setOptNumbers] = useState(true);  // sıra numarası
  const [optCodes, setOptCodes] = useState(false);     // personel kodu
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const docRef = useRef<HTMLDivElement | null>(null);

  const q = trpc.hr.shiftPlan.weekBoard.useQuery({ weekStart }, { staleTime: 30_000 });
  const prof = trpc.settings.companyProfile.get.useQuery({}, { staleTime: 5 * 60_000 });
  const d = q.data;

  const fmt = (iso: string) => `${iso.slice(8)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
  const today = new Date();
  const nowLabel = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;

  /** Vardiya bölümleri: satır = vardiya (+ istirahat), içinde pozisyon blokları. */
  const sections = useMemo(() => {
    if (!d) return [];
    const empById = new Map(d.employees.map((e) => [e.id, e]));
    const cellMap = new Map(d.cells.map((c) => [c.key, c.employeeIds]));
    const cols = [...d.positions.map((p) => ({ id: p.id, title: p.title }))];
    if (d.hasNoPosition) cols.push({ id: "none", title: "Pozisyon atanmamış" });

    const used = new Set<string>();
    for (const c of d.cells) used.add(c.key.split("|")[0]!);

    const rows: Array<{ key: string; label: string; time: string | null; total: number; blocks: Array<{ title: string; names: Array<{ id: string; name: string; code: string | null }> }> }> = [];
    const push = (key: string, label: string, time: string | null) => {
      const blocks = cols
        .map((c) => ({
          title: c.title,
          names: (cellMap.get(`${key}|${c.id}`) ?? [])
            .map((id) => {
              const e = empById.get(id);
              // `id` TASINIR: React anahtari ad/koda dayanamaz — ayni ada sahip iki personel
              // (ya da kodu olmayan iki kisi) mukerrer anahtar uretir ve React satirlari
              // birlestirip BIRINI EKRANA BASMAZ. Vardiya listesinde eksik isim = yanlis belge.
              return { id, name: e?.name ?? "—", code: e?.code ?? null };
            })
            .sort((a, b) => a.name.localeCompare(b.name, "tr")),
        }))
        .filter((b) => b.names.length > 0);
      const total = blocks.reduce((s, b) => s + b.names.length, 0);
      if (total > 0) rows.push({ key, label, time, total, blocks });
    };
    for (const s of d.shifts) if (used.has(s.id)) push(s.id, s.name, `${s.startTime}–${s.endTime}`);
    if (optOff && used.has("off")) push("off", "İstirahat", null);
    return rows;
  }, [d, optOff]);

  const co = prof.data;

  /**
   * JPEG — html2canvas BU BELGE üzerinde çalışır, panonun üzerinde DEĞİL.
   * Panoda `color-mix(in srgb, …)` kullanılıyor; html2canvas 1.4.1 bu CSS
   * fonksiyonunu tanımıyor ve "Attempting to parse an unsupported color function",
   * diyip çöküyordu. Bu sayfanın paleti düz hex/değişken — sorun kökten yok.
   * Ayrıca çıktı zaten sayfaya sığdırılmış profesyonel yerleşim.
   */
  const exportJpeg = async () => {
    const node = docRef.current;
    if (!node || !d) return;
    setErr(null); setBusy("jpeg");
    try {
      const { default: html2canvas } = await import("html2canvas");
      const shot = await html2canvas(node, {
        scale: 3, backgroundColor: "#ffffff", useCORS: true, logging: false,
        windowWidth: node.scrollWidth, windowHeight: node.scrollHeight,
      });
      const blob = await new Promise<Blob | null>((res) => shot.toBlob(res, "image/jpeg", 0.95));
      if (!blob) throw new Error("Görüntü oluşturulamadı");
      const fileName = `vardiya-plani-${d.weekStart}_${d.weekEnd}.jpg`;
      const file = new File([blob], fileName, { type: "image/jpeg" });
      const nav = typeof navigator !== "undefined" ? navigator : undefined;
      if (nav?.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: "Haftalık Vardiya Planı" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    } catch (e) {
      const x = e as { name?: string; message?: string };
      if (x?.name !== "AbortError") setErr(x?.message ?? "Görüntü oluşturulamadı");
    } finally { setBusy(null); }
  };
  /* Pano "JPEG" düğmesi buraya ?export=jpeg ile gelir → veri yüklenince BİR KEZ üret.
     Ref ile kilitlenir; yeniden render (seçenek değişimi vb.) tekrar tetiklemesin. */
  const autoRan = useRef(false);
  useEffect(() => {
    if (sp.get("export") !== "jpeg" || !d || autoRan.current) return;
    autoRan.current = true;
    void exportJpeg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);
  return (
    <div className="svp-root">
      <style>{`
        .svp-root { --ink:#14253E; --line:#d7dce5; --muted:#5b6472; --soft:#f2f5fa; background:#fff; color:#1a2230; }
        .svp-page { padding: 14px 18px 30px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 11px; }

        .svp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px;
                    border-bottom: 2px solid var(--ink); padding-bottom: 8px; margin-bottom: 12px; }
        .svp-title { font-size: 16px; font-weight: 800; color: var(--ink); letter-spacing:-0.01em; }
        .svp-range { font-size: 12px; font-weight: 700; color: var(--ink); margin-top: 2px;
                     font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .svp-sub { font-size: 10.5px; color: var(--muted); margin-top: 3px; }
        .svp-co { text-align:right; font-size: 10.5px; color: var(--muted); }
        .svp-co b { color: var(--ink); font-size: 12.5px; display:block; }
        .svp-logo { max-height: 44px; max-width: 120px; object-fit: contain; margin-bottom: 4px; }

        /* VARDİYA SÜTUNLARI: her vardiya DİKEY bir sütun, aralarında ayraç çizgisi.
           Gerçek <table> kullanılıyor çünkü tarayıcı uzun bir satırı sayfalar arasında
           bölerken sütun hizasını ve kenarlıkları korur; flex/CSS-columns bunu yapamaz.
           <thead> her sayfada tekrar eder → 2. sayfada hangi sütunun hangi vardiya
           olduğu kaybolmaz. */
        table.svp-grid { width: 100%; border-collapse: collapse; table-layout: fixed; }
        table.svp-grid thead { display: table-header-group; }
        table.svp-grid th {
          text-align: left; vertical-align: bottom;
          border-bottom: 2px solid var(--ink); padding: 3px 5px 4px;
        }
        table.svp-grid th .nm { display:block; font-size: 11px; font-weight: 800; color: var(--ink);
                                text-transform: uppercase; letter-spacing:.02em; }
        table.svp-grid th .tm { font-family:"JetBrains Mono", monospace; font-size: 8.5px; color: var(--muted); }
        table.svp-grid th .ct { float:right; font-family:"JetBrains Mono", monospace; font-size: 9px;
                                font-weight:700; color: var(--ink); }
        /* DİKEY AYRAÇ — vardiya sütunlarını birbirinden ayırır. İlk sütunda yok. */
        table.svp-grid th + th, table.svp-grid td + td { border-left: 1px solid var(--line); }
        table.svp-grid td { vertical-align: top; padding: 5px 5px 8px; }

        .svp-blk { margin-bottom: 7px; break-inside: auto; }
        .svp-blk h4 { font-size: 8.5px; font-weight: 800; letter-spacing:.04em; text-transform: uppercase;
                      color: var(--ink); margin: 0 0 2px; padding-bottom: 2px;
                      border-bottom: 1px solid var(--line); break-after: avoid; }
        .svp-blk .n { font-size: 9px; color: var(--muted); font-weight:600; }
        .svp-blk ol { margin: 0; padding-left: 13px; }
        .svp-blk li { font-size: 9px; line-height: 1.38; break-inside: avoid;
                      overflow-wrap: anywhere; }
        .svp-blk .cd { font-family:"JetBrains Mono", monospace; font-size: 8px; color: var(--muted); }
        .svp-blk ul.plain { margin:0; padding-left: 0; list-style: none; }
        .svp-empty { padding: 40px; text-align:center; color: var(--muted); }
        .svp-foot { margin-top: 10px; padding-top: 6px; border-top: 1px solid var(--line);
                    font-size: 9.5px; color: var(--muted); display:flex; justify-content:space-between; }

        .svp-toolbar { position: sticky; top:0; z-index:5; background:#fff; display:flex; gap:10px;
                       align-items:center; justify-content:space-between; padding: 9px 18px; border-bottom:1px solid var(--line); }
        .svp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px;
                   padding:7px 16px; border-radius:6px; cursor:pointer; font-family:inherit; }
        .svp-btn.sec { background:#fff; color: var(--ink); }
        .svp-opts { display:flex; align-items:center; gap:12px; flex-wrap:wrap; font-size:11.5px; color:#1a2230; }
        .svp-optlbl { font-size:10px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); }
        .svp-opts label { display:inline-flex; align-items:center; gap:5px; cursor:pointer; white-space:nowrap; }
        .svp-opts input { width:14px; height:14px; accent-color: var(--ink); cursor:pointer; }
        .svp-tb-actions { display:flex; gap:8px; flex-shrink:0; }
        .svp-err { margin: 8px 18px; padding: 8px 10px; border:1px solid #f0c2c2; background:#fdf3f3;
                   color:#8a2020; font-size:12px; border-radius:6px; }

        @media print {
          .svp-toolbar, .svp-err { display:none !important; }
          .svp-page { padding: 0; }
          /* Pozisyon başlığı yalnız başına kalmasın; isim satırı ortadan bölünmesin. */
          .svp-blk h4 { break-after: avoid; }
          .svp-blk li { break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 10mm; }
      `}</style>

      <div className="svp-toolbar">
        <div className="svp-opts">
          <span className="svp-optlbl">Belgede yer alsın:</span>
          <label><input type="checkbox" checked={optCompany} onChange={(e) => setOptCompany(e.target.checked)} />Firma künyesi</label>
          <label><input type="checkbox" checked={optOff} onChange={(e) => setOptOff(e.target.checked)} />İstirahat sütunu</label>
          <label><input type="checkbox" checked={optCounts} onChange={(e) => setOptCounts(e.target.checked)} />Kişi sayıları</label>
          <label><input type="checkbox" checked={optNumbers} onChange={(e) => setOptNumbers(e.target.checked)} />Sıra numarası</label>
          <label><input type="checkbox" checked={optCodes} onChange={(e) => setOptCodes(e.target.checked)} />Personel kodu</label>
        </div>
        <div className="svp-tb-actions">
          <button type="button" className="svp-btn sec" onClick={() => window.close()}>Kapat</button>
          <button type="button" className="svp-btn sec" onClick={exportJpeg} disabled={!d || busy !== null}>
            {busy === "jpeg" ? "Oluşturuluyor…" : "JPEG indir"}
          </button>
          <button type="button" className="svp-btn" onClick={() => window.print()} disabled={!d}>Yazdır / PDF</button>
        </div>
      </div>
      {err ? <div className="svp-err" role="alert">{err}</div> : null}
      <div className="svp-page" ref={docRef}>
        {q.isLoading ? (
          <div className="svp-empty">Yükleniyor…</div>
        ) : q.error ? (
          <div className="svp-empty">{q.error.message}</div>
        ) : !d || sections.length === 0 ? (
          <div className="svp-empty">Bu hafta için plan yok.</div>
        ) : (
          <>
            <div className="svp-head">
              <div>
                <div className="svp-title">HAFTALIK VARDİYA PROGRAMI</div>
                <div className="svp-range">{fmt(d.weekStart)} – {fmt(d.weekEnd)}</div>
                <div className="svp-sub">
                  {d.employees.length} personel · {sections.length} vardiya grubu
                  {d.weekPeriod?.status === "locked" ? " · KİLİTLİ" : d.weekPeriod?.status === "published" ? " · Yayınlandı" : " · Taslak"}
                </div>
              </div>
              <div className="svp-co">
                {optCompany ? (
                  <>
                    {co?.logoUrl ? <img className="svp-logo" src={co.logoUrl} alt="" /> : null}
                    <b>{co?.name ?? ""}</b>
                    {co?.address ? <div>{co.address}</div> : null}
                  </>
                ) : null}
                <div>Yazdırma: {nowLabel}</div>
              </div>
            </div>

            <table className="svp-grid">
              <thead>
                <tr>
                  {sections.map((sec) => (
                    <th key={sec.key} style={{ width: `${100 / sections.length}%` }}>
                      {optCounts ? <span className="ct">{sec.total} kişi</span> : null}
                      <span className="nm">{sec.label}</span>
                      {sec.time ? <span className="tm">{sec.time}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {sections.map((sec) => (
                    <td key={sec.key}>
                      {sec.blocks.map((b) => (
                        <div className="svp-blk" key={b.title}>
                          <h4>{b.title}{optCounts ? <span className="n"> ({b.names.length})</span> : null}</h4>
                          {optNumbers ? (
                            <ol>{b.names.map((n) => <li key={n.id}>{n.name}{optCodes && n.code ? <span className="cd"> · {n.code}</span> : null}</li>)}</ol>
                          ) : (
                            <ul className="plain">{b.names.map((n) => <li key={n.id}>{n.name}{optCodes && n.code ? <span className="cd"> · {n.code}</span> : null}</li>)}</ul>
                          )}
                        </div>
                      ))}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <div className="svp-foot">
              <span>STEPERP — Haftalık Vardiya Programı</span>
              <span>{fmt(d.weekStart)} – {fmt(d.weekEnd)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
